import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { merge, Observable, of as observableOf, Subject, Subscription } from 'rxjs';
import { catchError, map, startWith, switchMap } from 'rxjs/operators';
import { UserService } from '../../../../services/user.service';
import { BillingService } from '../../../../services/billing.service';
import { MatPaginator, MatTableDataSource, MatSort, MatSnackBar, MatDialog } from '@angular/material';
import { ViewParentPnrDialog } from '../../billing/dialog.viewParentPnr';
import {
  TransactionList,
  OthTransactionBillingPagedList,
  SelectModel,
  SummaryFieldElement
} from '../../../../models/transaction.model';
import { TransactionType, TransactionRecordStatus } from '../../../../shared/enum';
import { SelectionModel } from '@angular/cdk/collections';
import { AddMissingTransactionDialog } from '../../billing/dialog.addmissing';
import { TransactionDownloadFileDialog } from '../../dialog.downloadFile';
import { EditOtherGovernmentTransactionDialog } from '../../billing/other-government/dialog.edittransaction';
import { ExportOtherGovernmentTransactionDialog } from '../../billing/other-government/dialog.exporttransaction';

@Component({
  selector: 'app-other-government',
  templateUrl: './other-government.component.html',
  styleUrls: ['./other-government.component.css']
})
export class BillingOtherGovernmentComponent implements OnInit, OnDestroy {
  allSub: Subscription[] = [];
  errorMessage: string = '';
  userName: string = '';
  screenCode: string = '';
  isLoadingResults: boolean = true;
  pnrs: string;
  travelAgent: string;
  pukalNo: string;
  warrantNo: string;
  invoiceNo: string;
  agentCodes = [];
  accOffices = [];
  rcdStatus = [];
  fromDate: Date;
  toDate: Date;
  selectedAgentCode: string = '';
  selectedAccOffCode: string = '0';
  selectedRcdStsCode: string = '0';
  selectedSplitBooking: string;
  isSplitBooking: SelectModel[] = [
    { value: '', viewValue: '' },
    { value: '1', viewValue: 'Yes' },
    { value: '0', viewValue: 'No' }
  ];
  warrant_auth_code: string = 'OT';
  transactionList: TransactionList;
  displayedColumns: string[] = ['Select', 'Edit', 'RowNumber', 'PNR', 'BookingDateTime', 'TravelAgent', 'PukalNumber',
    'WarrantNumber', 'Object', 'PaymentDateTime', 'BaseFare', 'AirportTax', 'Vat', 'FuelSurcharge',
    'ServicesCharge', 'PaymentAmount', 'Status', 'Check', 'InvoiceNo', 'InvoiceDate', 'SplitBooking'];
  SummaryColumns: string[] = ['Records', 'LineCount', 'Percentage'];
  ELEMENT_DATA: SummaryFieldElement[];
  summaryDataSource: SummaryFieldElement[];
  data: OthTransactionBillingPagedList[];
  dataSource: MatTableDataSource<OthTransactionBillingPagedList>;
  selection: SelectionModel<OthTransactionBillingPagedList> = new SelectionModel<OthTransactionBillingPagedList>(true, []);
  resultsLength: number = 0;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  searchFieldData: Observable<Array<any>>;
  recordChange$: Subject<{}> = new Subject();

  constructor(private database: BillingService,
    private userService: UserService,
    public snackBar: MatSnackBar,
    public dialog: MatDialog) {
    let user = this.userService.getUser();
    this.userName = user.username;
    this.screenCode = user.screenCode;

    this.database.getTransactionSearchFieldDataByWarrantAuthCode(this.warrant_auth_code)
      .subscribe(
        (value) => {
          this.agentCodes = value.agentCodes;
          this.selectedAgentCode = value.agentCodes[0].Airline_Agency_Code;
          this.accOffices = value.accountOffices;
          this.rcdStatus = value.recordStatuses;
          this.fromDate = new Date(value.paymentFrom);
          this.toDate = new Date(value.paymentTo);
        },
        (error) => {
          this.errorMessage = <any>error;
          this.openSnackBar(this.errorMessage, '');
        });
  }

  ngOnInit() {
    this.transactionList = {
      agentCode: this.selectedAgentCode,
      accOffCode: this.selectedAccOffCode,
      paymentFrom: this.fromDate,
      paymentTo: this.toDate,
      sqlPNR: '',
      travelAgent: '',
      pukalNo: '',
      warrantNumber: '',
      recordStatus: '0',
      page: null,
      isSplitBooking: '',
      invoiceNo: '',
      PageSize: null,
      transactionType: TransactionType.OthAgency
    };

    merge(this.recordChange$)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;

          return this.database!.getAllTransactionListRecord(this.transactionList);
        }),
        map(data => {
          this.isLoadingResults = false;
          this.resultsLength = data.countJANTransactionBillingList;

          this.ELEMENT_DATA = [
            { name: 'Total Records', LineCount: data.TotalRecords.toString(), Percentage: null },
            { name: 'Checked Records', LineCount: data.TotalChecked.toString(), Percentage: data.CheckedPercent },
            { name: 'Unchecked Records', LineCount: data.TotalUnchecked.toString(), Percentage: data.UncheckedPercent },
            { name: 'Total Amount (MYR)', LineCount: data.TotalAmount, Percentage: null }
          ]
          this.summaryDataSource = this.ELEMENT_DATA;

          this.dataSource = new MatTableDataSource<OthTransactionBillingPagedList>(data.janTransactionBillingPagedList)
          this.dataSource.paginator = this.paginator;

          return data.janTransactionBillingPagedList;
        }),
        catchError((error) => {
          this.isLoadingResults = false;
          this.errorMessage = <any>error;
          this.openSnackBar(this.errorMessage, '');
          return observableOf([]);
        })
      ).subscribe(data => this.data = data);
  }

  /** Whether the number of selected elements matches the total number of rows. */
  isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource == null ? 0 : this.dataSource.data.length;

    return numSelected === numRows;
  }

  /** Selects all rows if they are not all selected; otherwise clear selection. */
  masterToggle() {
    this.isAllSelected() ?
      this.selection.clear() :
      this.data.forEach(row => this.selection.select(row));
  }

  includeOnClick() {
    if (this.selection.selected.length === 0) {
      this.openSnackBar('No transaction is selected. Please make sure at least one transaction is selected.', '');
      return;
    }
    let cfm = confirm("Are you sure you want to include those selected transaction(s)?");

    if (cfm) {
      this.isLoadingResults = true;

      let ids = "";
      for (let i = 0; i < this.selection.selected.length; i++) {
        ids += this.selection.selected[i].Bill_Id + ',';
      }

      let recordIDs = {
        ID: ids,
        RecordStatus: TransactionRecordStatus.Status_NEW
      }

      let update_record_sub = this.database.updateJANTransactionRecordStatus(recordIDs)
        .subscribe(
          (data) => {
            if (data > 0) {
              this.isLoadingResults = false;
              this.selection.clear();
              this.searchTransaction();

              this.openSnackBar('Selected transaction(s) Included.', '');
            } else {
              this.isLoadingResults = false;
              this.openSnackBar('Include transaction failed.', '');
            }
          },
          (error) => {
            this.errorMessage = <any>error;
            this.openSnackBar(this.errorMessage, '');
          });

      //Adds into array for each subscribe
      this.allSub.push(update_record_sub);
    }
  }

  excludeOnClick() {
    if (this.selection.selected.length === 0) {
      this.openSnackBar('No transaction is selected. Please make sure at least one transaction is selected.', '');
      return;
    }
    let cfm = confirm("Are you sure you want to exclude those selected transaction(s)?");

    if (cfm) {
      this.isLoadingResults = true;

      let ids = "";
      for (let i = 0; i < this.selection.selected.length; i++) {
        ids += this.selection.selected[i].Bill_Id + ',';
      }

      let recordIDs = {
        ID: ids,
        RecordStatus: TransactionRecordStatus.Status_EXCLUDED
      }

      let update_record_sub = this.database.updateJANTransactionRecordStatus(recordIDs)
        .subscribe(
          (data) => {
            if (data > 0) {
              this.isLoadingResults = false;
              this.selection.clear();
              this.searchTransaction();

              this.openSnackBar('Selected transaction(s) Excluded.', '');
            } else {
              this.isLoadingResults = false;
              this.openSnackBar('Exclude transaction failed.', '');
            }
          },
          (error) => {
            this.errorMessage = <any>error;
            this.openSnackBar(this.errorMessage, '');
          });

      //Adds into array for each subscribe
      this.allSub.push(update_record_sub);
    }
  }

  addMissingTransaction() {
    let dialogRef = this.dialog.open(AddMissingTransactionDialog, {
      width: '60%',
      disableClose: true,
      data: {
        warrantauthcode: this.warrant_auth_code
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Transaction saved') {
        this.searchTransaction();
      } else if (result === 'close') {
      }
    });
  }

  exportTransactionList() {
    let cfm = confirm("Are you sure you want to export the List?");

    if (cfm) {
      this.transactionList = {
        Url: "ExportOthSelectedTransactionList",
        agentCode: this.selectedAgentCode,
        accOffCode: this.selectedAccOffCode,
        paymentFrom: this.fromDate,
        paymentTo: this.toDate,
        sqlPNR: this.pnrs,
        travelAgent: this.travelAgent,
        pukalNo: this.pukalNo,
        warrantNumber: this.warrantNo,
        recordStatus: this.selectedRcdStsCode,
        page: null,
        isSplitBooking: this.selectedSplitBooking,
        invoiceNo: this.invoiceNo,
        PageSize: null,
        transactionType: TransactionType.OthAgency
      };

      let dialogRef = this.dialog.open(TransactionDownloadFileDialog, {
        width: '250px',
        disableClose: true,
        data: { downloadParameter: this.transactionList }
      });

      dialogRef.afterClosed().subscribe(result => {
        switch (result.status) {
          case -1:
            this.openSnackBar(result.message, '');
            break;
          case 1:
            this.openSnackBar(result.message, '');
            break;
        }
      });
    }
  }

  searchTransaction() {
    this.dataSource = new MatTableDataSource<OthTransactionBillingPagedList>();
    this.paginator.pageIndex = 0;

    this.transactionList = {
      agentCode: this.selectedAgentCode,
      accOffCode: this.selectedAccOffCode,
      paymentFrom: this.fromDate,
      paymentTo: this.toDate,
      sqlPNR: this.pnrs,
      travelAgent: this.travelAgent,
      pukalNo: this.pukalNo,
      warrantNumber: this.warrantNo,
      recordStatus: this.selectedRcdStsCode,
      page: null,
      isSplitBooking: this.selectedSplitBooking,
      invoiceNo: this.invoiceNo,
      PageSize: null,
      transactionType: TransactionType.OthAgency
    };

    this.recordChange$.next();
  }

  bookingDateFromOnChange(date: Date) {
    this.fromDate = date;
  }

  bookingDateToOnChange(date: Date) {
    this.toDate = date;
  }

  checkOnChange(e, d) {
    this.isLoadingResults = true;

    let updateTranCheck = {
      BillTranCheck: e.checked,
      BillId: d.Bill_Id,
      AccAirlineAgencyCode: this.selectedAgentCode,
      PaymentFrom: this.fromDate,
      PaymentTo: this.toDate,
      AccOffCode: this.selectedAccOffCode,
      RcdSts: this.selectedRcdStsCode,
      BillPnr: this.pnrs,
      BillAccNo: this.pukalNo,
      BillSerialNo: this.warrantNo,
      BillAgencyCode: this.travelAgent,
      IsSplitBooking: this.selectedSplitBooking === undefined ? "" : this.selectedSplitBooking,
      InvoiceNo: this.invoiceNo
    }

    let update_check = this.database.UpdateOTHCheckTransaction(updateTranCheck)
      .subscribe(
        (value) => {
          this.isLoadingResults = false;

          this.dataSource.data.map(el => {
            if (el.Bill_Id === d.Bill_Id)
              el.Bill_Tran_Check = e.checked;
          });

          this.ELEMENT_DATA = [
            { name: 'Total Records', LineCount: value.TotalRecords.toString(), Percentage: null },
            { name: 'Checked Records', LineCount: value.TotalChecked.toString(), Percentage: value.CheckedPercent },
            { name: 'Unchecked Records', LineCount: value.TotalUnchecked.toString(), Percentage: value.UncheckedPercent },
            { name: 'Total Amount (MYR)', LineCount: value.TotalAmount, Percentage: null }
          ]

          this.summaryDataSource = this.ELEMENT_DATA;
        },
        (error) => {
          this.isLoadingResults = false;
          this.errorMessage = <any>error;
          this.openSnackBar(this.errorMessage, '');
        });

    //Adds into array for each subscribe
    this.allSub.push(update_check);
  }

  openViewParentPnrDialog(parentpnr: string): void {
    let dialogRef = this.dialog.open(ViewParentPnrDialog, {
      width: '30%',
      disableClose: true,
      data: { selectedParentPnr: parentpnr }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'Close') { }
    });
  }

  openEditBillingDialog(id: number, pnr: string) {
    let dialogRef = this.dialog.open(EditOtherGovernmentTransactionDialog, {
      width: '60%',
      disableClose: true,
      data: {
        selectedBillId: id,
        selectedBillPnr: pnr
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'close') {
      } else if (result === 'Edit successful') {
        this.searchTransaction();
      }
    });
  }


  exportOthTransactionList() {
    let dialogRef = this.dialog.open(ExportOtherGovernmentTransactionDialog, {
      width: '60%',
      disableClose: true,
      data: {
        agentCode: this.selectedAgentCode,
        accOffCode: this.selectedAccOffCode,
        recordStatus: this.selectedRcdStsCode,
        sqlPNR: this.pnrs,
        warrantNo: this.warrantNo,
        invoiceNo: this.invoiceNo,
        paymentFrom: this.fromDate,
        paymentTo: this.toDate
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      switch (result.status) {
        case -1:
          this.openSnackBar(result.message, '');
          break;
        case 1:
          this.openSnackBar(result.message, '');
          this.searchTransaction();
          break;
      }
    });
  }


  setDisableToolTip(isSplitBooking: string) {
    return isSplitBooking === 'Yes' ? false : true;
  }

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, {
      duration: 3000,
    });
  }

  ngOnDestroy() {
    for (let sub of this.allSub) {
      sub.unsubscribe();
    }
  }
}
